import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Globe, CheckCircle2, Clock, AlertCircle,
  RefreshCw, Loader2, Sparkles, Settings,
  Archive, ArchiveRestore, ExternalLink, FileText, Download,
  BarChart3, Brain, ScanSearch, Eye, Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ── Visualization components ──────────────────────────────────────────────
import { AuditScoreCard, CategoryDonut, ImpactEffortMatrix, GRADE_COLORS } from './onpage/SEOCharts';
import { PageDataPanel, SectionScoresCard, CoreWebVitalsPanel, SecurityHeadersPanel, ReadabilityPanel, RobotsSitemapPanel, AIReadinessPanel, UsabilityPanel, PerformanceResourcePanel, TechnologyPanel, SocialLinksPanel, RedirectsMixedContentPanel, HreflangPanel, CodeQualityPanel, RenderBlockingPanel, CachingMinificationPanel, BrokenLinksPanel, SSLCertPanel, AnalyticsCDNPanel, FontOptimizationPanel, IframesPanel } from './onpage/SEOPageData';
import { RecommendationsPanel, ScanFormDialog } from './onpage/SEORecommendations';

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay },
});

const INITIAL_STEPS = Object.freeze({
  crawl: { status: 'idle', data: null },
  score: { status: 'idle', data: null },
  recommend: { status: 'idle', data: null },
});

const EMPTY_RESULTS = Object.freeze({ crawl: null, score: null, recommend: null });

// FastAPI `detail` may be a plain string or a structured object (e.g. package-gate
// errors: { error, message, feature, required, current }). Always coerce to a
// renderable string so it never reaches JSX/toast as an object.
const detailMessage = (detail, fallback = null) => {
  if (detail && typeof detail === 'object') return detail.message || fallback;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const buildStats = (scan) => {
  if (!scan) return null;
  const recs = scan.recommendations || [];
  return {
    total: recs.length,
    pending: recs.filter((r) => r.status === 'pending').length,
    client_approved: recs.filter((r) => r.status === 'client_approved').length,
    approved: recs.filter((r) => r.status === 'approved').length,
    implemented: recs.filter((r) => r.status === 'implemented').length,
    high: recs.filter((r) => r.severity === 'high').length,
    critical: recs.filter((r) => r.priority_group === 'critical').length,
    important: recs.filter((r) => r.priority_group === 'important').length,
    minor: recs.filter((r) => r.priority_group === 'minor').length,
    quick_win: recs.filter((r) => r.priority_group === 'quick_win').length,
  };
};

/* ─── ScanHistoryItem ────────────────────────────────────────────────────── */
const ScanHistoryItem = ({ scan, active, onSelect, onArchive, onRestore, onDelete, archived }) => {
  const score = scan.audit_scores?.overall_score;
  const grade = scan.audit_scores?.grade;
  const recCount = scan.recommendations_count ?? scan.recommendations?.length ?? 0;
  const highCount = scan.high_priority ?? scan.recommendations?.filter((r) => r.severity === 'high').length ?? 0;
  const domain = scan.url ? scan.url.replace(/^https?:\/\//, '').split('/')[0] : '';
  const path = scan.url ? '/' + scan.url.replace(/^https?:\/\//, '').split('/').slice(1).join('/') : '';
  const status = scan.status || 'complete';
  const isGenerating = !archived && status !== 'complete';
  const statusHint =
    status === 'crawled' ? 'Running SEO score & AI…' :
    status === 'scored' ? 'Generating AI recommendations…' :
    isGenerating ? 'In progress…' : null;

  return (
    <div
      role={archived ? undefined : 'button'}
      tabIndex={archived ? undefined : 0}
      onClick={archived ? undefined : () => onSelect(scan)}
      onKeyDown={archived ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(scan); } }}
      className={`p-3 rounded-xl transition-all group relative ${
        archived ? '' : 'cursor-pointer '
      }${
        active
          ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700'
          : 'bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500">
          {new Date(scan.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
        </p>
        <div className="flex items-center gap-1">
          {score != null && (
            <span className={`text-xs font-bold ${GRADE_COLORS[grade] || 'text-slate-500'}`}>{score}</span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); archived ? onRestore(scan.scan_id, e) : onArchive(scan.scan_id, e); }}
            className="opacity-0 group-hover:opacity-100 p-1 rounded-md transition-all text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            title={archived ? 'Restore scan' : 'Archive scan'}
          >
            {archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(scan, e); }}
            className="opacity-0 group-hover:opacity-100 p-1 rounded-md transition-all text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Delete scan"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {domain && (
        <p className="text-xs text-slate-600 dark:text-slate-400 truncate mt-1" title={scan.url}>
          {domain}<span className="text-slate-400">{path && path !== '/' ? path : ''}</span>
        </p>
      )}
      {isGenerating && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/80 dark:bg-indigo-950/40 px-2.5 py-2">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold text-indigo-800 dark:text-indigo-200">Generating</p>
            {statusHint && (
              <p className="text-[10px] text-indigo-600/90 dark:text-indigo-400/90 leading-tight">{statusHint}</p>
            )}
          </div>
          <Loader2 className="w-4 h-4 text-indigo-500 animate-spin shrink-0" />
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        <Badge variant="outline" className="text-[11px] px-1.5 py-0">
          {isGenerating ? '— issues' : `${recCount} issues`}
        </Badge>
        {!isGenerating && highCount > 0 && (
          <Badge variant="outline" className="text-[11px] px-1.5 py-0 bg-red-50 text-red-600 border-red-200">
            {highCount} high
          </Badge>
        )}
        {grade && (
          <Badge variant="outline" className={`text-[11px] px-1.5 py-0 ${GRADE_COLORS[grade]}`}>
            {grade}
          </Badge>
        )}
      </div>
      {!archived && (
        <div
          className={`mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-lg pointer-events-none ${
            active
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-800/40 dark:text-indigo-300'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
          }`}
        >
          <Eye className="w-3.5 h-3.5" />
          {active ? (isGenerating ? 'Viewing progress' : 'Viewing') : (isGenerating ? 'Open progress' : 'View Report')}
        </div>
      )}
    </div>
  );
};

/* ─── StatCard ───────────────────────────────────────────────────────────── */
const StatCard = ({ value, label, colorClass = '', delay = 0 }) => (
  <motion.div {...fadeUp(delay)}>
    <Card className="gds-card">
      <CardContent className="p-4 text-center">
        <p className={`text-3xl font-bold font-heading ${colorClass}`}>{value}</p>
        <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      </CardContent>
    </Card>
  </motion.div>
);

/* ─── Scan Progress Phases (kept for reference) ────────────────────────── */
const STEP_META = [
  { Icon: ScanSearch, color: 'text-blue-500', label: 'Crawl & Extract', detail: 'Fetch HTML, parse meta tags, headings, images, links, robots.txt & sitemap' },
  { Icon: BarChart3, color: 'text-emerald-500', label: 'SEO Scoring', detail: 'Run 12-section deterministic audit rubric (title, content, links, technical…)' },
  { Icon: Brain, color: 'text-purple-500', label: 'AI Recommendations', detail: '4 specialist AI agents analyse in parallel, then synthesise a prioritised action plan' },
];

/* ─── Step Summary Builders ────────────────────────────────────────────── */
function buildCrawlSummary(pd) {
  if (!pd) return '';
  const parts = [];
  parts.push(`Successfully crawled and extracted data from the page.`);
  parts.push(`Found ${pd.word_count || 0} words of content across ${(pd.h1?.length || 0) + (pd.h2?.length || 0) + (pd.h3?.length || 0)} headings.`);
  if (pd.title) parts.push(`Title tag: "${pd.title}" (${pd.title_length || pd.title.length} chars).`);
  else parts.push('No title tag found — this is a critical SEO issue.');
  if (pd.meta_description) parts.push(`Meta description present (${pd.meta_description_length || pd.meta_description.length} chars).`);
  else parts.push('Meta description is missing.');
  const imgCount = pd.images_detailed?.length || 0;
  const missingAlt = pd.images_detailed?.filter(i => !i.alt).length || 0;
  if (imgCount > 0) parts.push(`${imgCount} images detected${missingAlt > 0 ? `, ${missingAlt} missing alt text` : ', all have alt text'}.`);
  parts.push(`${pd.internal_links?.length || 0} internal links, ${pd.external_links?.length || 0} external links.`);
  if (pd.schema_markup?.length > 0) parts.push(`${pd.schema_markup.length} structured data (JSON-LD) blocks found.`);
  if (pd.robots_txt?.exists) parts.push('robots.txt present.');
  if (pd.sitemap?.exists) parts.push(`XML sitemap found with ${pd.sitemap.url_count || '?'} URLs.`);
  if (pd.is_https) parts.push('HTTPS enabled.');
  else parts.push('Site is not using HTTPS — security concern.');
  return parts.join(' ');
}

function buildScoreSummary(scores) {
  if (!scores) return '';
  const parts = [];
  parts.push(`Your page scored ${scores.overall_score}/100 (Grade ${scores.grade}).`);
  const sections = scores.sections || {};
  const weak = Object.entries(sections).filter(([, s]) => s.score < 5).map(([, s]) => s.label || 'Unknown');
  const strong = Object.entries(sections).filter(([, s]) => s.score >= 8).map(([, s]) => s.label || 'Unknown');
  if (strong.length > 0) parts.push(`Strengths: ${strong.slice(0, 3).join(', ')}.`);
  if (weak.length > 0) parts.push(`Needs improvement: ${weak.slice(0, 3).join(', ')}.`);
  else parts.push('No critical weaknesses found in the rubric.');
  return parts.join(' ');
}

function buildRecommendSummary(data) {
  if (!data?.summary) return '';
  const s = data.summary;
  const parts = [];
  parts.push(`Generated ${s.total} actionable recommendations.`);
  if (s.high > 0) parts.push(`${s.high} are high priority — these should be addressed first.`);
  if (s.medium > 0) parts.push(`${s.medium} medium priority items for ongoing improvement.`);
  if (s.low > 0) parts.push(`${s.low} low priority suggestions for fine-tuning.`);
  return parts.join(' ');
}

/* ─── Scan Steps Accordion ─────────────────────────────────────────────── */
const ScanStepsAccordion = ({ steps, stepResults, onRunStep, anyRunning, isAutoScan }) => {
  return (
    <div className="space-y-4">
      {STEP_META.map((meta, idx) => {
        const key = ['crawl', 'score', 'recommend'][idx];
        const step = steps[key];
        /* Use separate stepResults for display — never wiped during an active scan */
        const resultData = stepResults?.[key] ?? null;
        const StepIcon = meta.Icon;
        const isDone = step.status === 'done';
        const isRunning = step.status === 'running';
        const isError = step.status === 'error';
        const isIdle = step.status === 'idle';
        const prevKey = idx > 0 ? ['crawl', 'score', 'recommend'][idx - 1] : null;
        const prevDone = !prevKey || steps[prevKey].status === 'done';
        /* In auto-scan mode never show manual Run buttons — steps run automatically */
        const canRun = !isAutoScan && prevDone && !anyRunning && !isDone;

        return (
          <Card key={key} className={`gds-card overflow-hidden transition-all ${
            isDone ? 'border-emerald-200 dark:border-emerald-800' :
            isRunning ? 'border-indigo-300 dark:border-indigo-700' :
            (isIdle && isAutoScan) ? 'opacity-60' : ''
          }`}>
            {isRunning && (
              <div className="relative h-1 bg-slate-100 dark:bg-slate-800">
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-r-full"
                  style={{ background: 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899)' }}
                  animate={{ width: ['10%', '60%', '90%', '60%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
            )}
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isDone ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                    isRunning ? 'bg-indigo-100 dark:bg-indigo-900/30' :
                    'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {isDone ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : isRunning ? (
                      <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                    ) : (
                      <StepIcon className={`w-5 h-5 ${(prevDone && !isAutoScan) ? meta.color : 'text-slate-400'}`} />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Step {idx + 1}</span>
                      {isDone && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">Done</Badge>}
                      {isRunning && <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0">Running</Badge>}
                      {isError && <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">Failed</Badge>}
                      {isIdle && isAutoScan && <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 text-[10px] px-1.5 py-0">Waiting</Badge>}
                    </div>
                    <h3 className="font-heading font-semibold">{meta.label}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{meta.detail}</p>
                  </div>
                </div>
                {!isDone && !isRunning && !isAutoScan && (
                  <Button
                    size="sm"
                    variant={canRun ? 'default' : 'outline'}
                    className={canRun ? 'gds-btn-primary' : ''}
                    disabled={!canRun}
                    onClick={() => onRunStep(key)}
                  >
                    <StepIcon className="w-4 h-4 mr-1.5" />
                    Run
                  </Button>
                )}
              </div>

              {/* Inline error message when step fails */}
              {isError && step.errorMessage && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800"
                >
                  <p className="text-sm text-red-700 dark:text-red-300">{step.errorMessage}</p>
                  <button
                    className="text-sm font-medium text-red-600 dark:text-red-400 mt-1 underline hover:no-underline"
                    onClick={() => onRunStep(key)}
                  >
                    Try again
                  </button>
                </motion.div>
              )}

              {/* Inline results preview — uses stepResults which is never wiped mid-scan */}
              {isDone && resultData && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-3">
                  {key === 'crawl' && resultData.page_data && (
                    <>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {buildCrawlSummary(resultData.page_data)}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { label: 'Words', value: resultData.page_data.word_count || 0 },
                          { label: 'Headings', value: (resultData.page_data.h1?.length || 0) + (resultData.page_data.h2?.length || 0) + (resultData.page_data.h3?.length || 0) },
                          { label: 'Images', value: resultData.page_data.images_detailed?.length || 0 },
                          { label: 'Links', value: (resultData.page_data.internal_links?.length || 0) + (resultData.page_data.external_links?.length || 0) },
                        ].map((m) => (
                          <div key={m.label} className="text-center p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                            <p className="text-lg font-bold font-heading">{m.value}</p>
                            <p className="text-xs text-slate-500">{m.label}</p>
                          </div>
                        ))}
                      </div>
                      {resultData.page_data.title && (
                        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                          <p className="text-xs font-semibold text-blue-600 mb-1">SERP Preview</p>
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-200 truncate">{resultData.page_data.title}</p>
                          <p className="text-xs text-emerald-700 truncate">{resultData.page_data.url}</p>
                          {resultData.page_data.meta_description && (
                            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{resultData.page_data.meta_description}</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {key === 'score' && resultData.audit_scores && (
                    <>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {buildScoreSummary(resultData.audit_scores)}
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[72px]">
                          <p className={`text-3xl font-bold font-heading ${GRADE_COLORS[resultData.audit_scores.grade] || ''}`}>
                            {resultData.audit_scores.overall_score}
                          </p>
                          <p className="text-xs text-slate-500">Score ({resultData.audit_scores.grade})</p>
                        </div>
                        <div className="flex-1 space-y-1.5">
                          {Object.entries(resultData.audit_scores.sections || {}).slice(0, 6).map(([k, s]) => (
                            <div key={k} className="flex items-center gap-2">
                              <span className="text-[11px] text-slate-500 w-24 truncate">{s.label || k}</span>
                              <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${s.score >= 7 ? 'bg-emerald-500' : s.score >= 4 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${(s.score / 10) * 100}%` }}
                                />
                              </div>
                              <span className="text-[11px] font-semibold w-6 text-right">{s.score}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {key === 'recommend' && resultData.summary && (
                    <>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {buildRecommendSummary(resultData)}
                      </p>
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { label: 'Total', value: resultData.summary.total, cls: '' },
                          { label: 'High', value: resultData.summary.high, cls: 'text-red-600' },
                          { label: 'Medium', value: resultData.summary.medium, cls: 'text-amber-600' },
                          { label: 'Low', value: resultData.summary.low, cls: 'text-slate-600' },
                        ].map((m) => (
                          <div key={m.label} className="text-center p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                            <p className={`text-lg font-bold font-heading ${m.cls}`}>{m.value}</p>
                            <p className="text-xs text-slate-500">{m.label}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function OnPageSEODashboard() {
  const { user } = useAuth();
  const { selectedProjectId } = useProject();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recommendationsRef = useRef(null);
  const [highlightRecId, setHighlightRecId] = useState(null);
  const onpageScanTask = useTaskState('onpage-scan');
  const [loading, setLoading] = useState(true);
  const [scans, setScans] = useState([]);
  const [currentScan, setCurrentScan] = useState(null);
  const [userWebsite, setUserWebsite] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedScans, setArchivedScans] = useState([]);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scanFormOpen, setScanFormOpen] = useState(false);
  const [scanFormData, setScanFormData] = useState({});

  const [scanSteps, setScanSteps] = useState(INITIAL_STEPS);
  const [activeScanId, setActiveScanId] = useState(null);
  const [isAutoScan, setIsAutoScan] = useState(false);
  /* stepResults stores collected data separately — never wiped during an active scan */
  const [stepResults, setStepResults] = useState(EMPTY_RESULTS);
  const allIdle = Object.values(scanSteps).every((s) => s.status === 'idle');
  const anyRunning = Object.values(scanSteps).some((s) => s.status === 'running');
  const allDone = Object.values(scanSteps).every((s) => s.status === 'done');

  const isClient = user?.role === 'client';
  /* Stage 2 (final approve/reject) = execution team + PM/Admin.
     Stage 3 (execute / mark done) = execution team + Admin. */
  const canFinalApprove = ['admin', 'project_manager', 'execution_team'].includes(user?.role);
  const canImplement = ['admin', 'execution_team'].includes(user?.role);

  /* ── When all steps done, load the full scan as currentScan ── */
  useEffect(() => {
    if (allDone && activeScanId) {
      /* Ensure the global task is cleared before resetting local state so
         scanSteps doesn't re-inflate a phantom "running" UI. */
      if (onpageScanTask.running) onpageScanTask.complete();
      api.get(`/onpage/scans/${activeScanId}`).then((r) => {
        setCurrentScan(r.data);
        /* Clear activeScanId FIRST so the accordion condition collapses
           atomically with the step reset — avoids a 1-render flicker where
           activeScanId && !allDone && !allIdle could still be true. */
        setActiveScanId(null);
        setScanSteps(INITIAL_STEPS);
        setStepResults(EMPTY_RESULTS);
        setIsAutoScan(false);
        api.get('/onpage/scans').then((sr) => setScans(sr.data)).catch(() => {});
      }).catch(() => {
        setActiveScanId(null);
        setScanSteps(INITIAL_STEPS);
        setStepResults(EMPTY_RESULTS);
        setIsAutoScan(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone, activeScanId]);

  /* Remount recovery: task is still running but local scanSteps are all idle.
     Hydrate scanSteps from task phase and restore stepResults for completed steps.
     Note: we hydrate even when scanId is not yet set (e.g., navigated away during
     the initial crawl POST) so the accordion shows progress instead of the empty
     "Run AI Scan" card. activeScanId is set later when scanId arrives. */
  useEffect(() => {
    const sid = onpageScanTask.scanId;

    if (onpageScanTask.running && !activeScanId && allIdle) {
      const p = typeof onpageScanTask.phase === 'number' ? onpageScanTask.phase : 0;
      if (sid) setActiveScanId(sid);
      setIsAutoScan(true);
      setScanSteps({
        crawl: { status: p === 0 ? 'running' : 'done', data: null },
        score: { status: p === 1 ? 'running' : p > 1 ? 'done' : 'idle', data: null },
        recommend: { status: p === 2 ? 'running' : 'idle', data: null },
      });
      // Repopulate stepResults for already-completed steps so inline previews render
      if (sid && p > 0) {
        api.get(`/onpage/scans/${sid}`).then((res) => {
          const scan = res.data;
          setStepResults((prev) => ({
            ...prev,
            crawl: prev.crawl || scan,
            score: p > 1 ? (prev.score || scan) : prev.score,
          }));
        }).catch(() => {});
      }
      return;
    }

    // Late scanId arrival: hydration already happened, but scanId only became
    // available after the initial crawl POST returned. Capture it now.
    if (onpageScanTask.running && sid && !activeScanId) {
      setActiveScanId(sid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onpageScanTask.running, onpageScanTask.scanId, activeScanId, allIdle]);

  /* Phase-tracking: keep scanSteps in sync when the background task advances phase
     after a remount recovery (e.g. score finished while user was on another tab). */
  useEffect(() => {
    if (!activeScanId || !onpageScanTask.running) return;
    const p = typeof onpageScanTask.phase === 'number' ? onpageScanTask.phase : 0;
    setScanSteps((prev) => {
      // Only update if this is actually a phase advance (avoids clobbering error states)
      const currentRunningKey = Object.keys(prev).find((k) => prev[k].status === 'running');
      const phaseKey = ['crawl', 'score', 'recommend'][p];
      if (currentRunningKey === phaseKey) return prev;
      return {
        crawl: { status: p === 0 ? 'running' : 'done', data: null },
        score: { status: p === 1 ? 'running' : p > 1 ? 'done' : 'idle', data: null },
        recommend: { status: p === 2 ? 'running' : 'idle', data: null },
      };
    });
    // Fetch scan to populate newly-completed step's results
    if (p > 0) {
      api.get(`/onpage/scans/${activeScanId}`).then((res) => {
        const scan = res.data;
        setStepResults((prev) => ({
          ...prev,
          crawl: prev.crawl || scan,
          score: p > 1 ? (prev.score || scan) : prev.score,
        }));
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onpageScanTask.phase, activeScanId, onpageScanTask.running]);

  /* Recovery: task marked complete externally (e.g. handleRunFullScan finished
     on a prior component instance after navigation) while this instance still
     shows a step as "running".  Fetch the finished scan and clean up. */
  useEffect(() => {
    if (!onpageScanTask.running && activeScanId && !allDone) {
      api.get(`/onpage/scans/${activeScanId}`).then((r) => {
        if (r.data.status === 'complete') {
          setCurrentScan(r.data);
          setActiveScanId(null);
          setScanSteps(INITIAL_STEPS);
          setStepResults(EMPTY_RESULTS);
          setIsAutoScan(false);
          api.get('/onpage/scans').then((sr) => setScans(sr.data)).catch(() => {});
        }
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onpageScanTask.running, activeScanId, allDone]);

  /* ── Poll for CWV + usability backfill when current scan has pending CWV ── */
  useEffect(() => {
    if (!currentScan?.scan_id) return;
    const cwv = currentScan?.page_data?.core_web_vitals;
    if (cwv?.available || !cwv?.pending) return;
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 20 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        if (cancelled) break;
        try {
          const res = await api.get(`/onpage/scans/${currentScan.scan_id}/cwv`);
          if (res.data?.available || !res.data?.pending) {
            const { usability, ...cwvData } = res.data;
            setCurrentScan((prev) => {
              if (!prev) return prev;
              const updatedPageData = { ...prev.page_data, core_web_vitals: cwvData };
              if (usability) updatedPageData.usability = usability;
              return { ...prev, page_data: updatedPageData };
            });
            break;
          }
        } catch { break; }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [currentScan?.scan_id]);

  /* ── Data fetching ──── */
  useEffect(() => { fetchUserData(); }, []);

  /* ── Deep link from a notification: ?scan=<id>&rec=<id> ──
     Select the linked scan, then scroll to + highlight the specific
     recommendation that needs review/final approval. */
  const deepLinkScan = searchParams.get('scan');
  const deepLinkRec = searchParams.get('rec');
  useEffect(() => {
    if (!deepLinkScan || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/onpage/scans/${deepLinkScan}`);
        if (cancelled) return;
        setCurrentScan(res.data);
        setActiveScanId(null);
        setScanSteps(INITIAL_STEPS);
        setHighlightRecId(deepLinkRec || null);
      } catch {
        /* scan not found / no access — fall back to default view */
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkScan, deepLinkRec, loading]);

  /* Scroll to the AI Recommendations section once the linked scan renders. */
  useEffect(() => {
    if (!highlightRecId || !currentScan) return;
    const t = setTimeout(() => {
      recommendationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
    return () => clearTimeout(t);
  }, [highlightRecId, currentScan]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const meRes = await api.get('/auth/me');
      let website = meRes.data?.website;
      if (!website) {
        try {
          const cmsRes = await api.get('/cms/status');
          if (cmsRes.data?.status === 'connected') website = cmsRes.data.site_url;
        } catch (_) {}
      }
      setUserWebsite(website);
      const scansRes = await api.get('/onpage/scans');
      setScans(scansRes.data);
      if (scansRes.data.length > 0) setCurrentScan(scansRes.data[0]);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedScans = async () => {
    try {
      const res = await api.get('/onpage/scans', { params: { archived: true } });
      setArchivedScans(res.data);
    } catch (err) {
      console.error('Error fetching archived scans:', err);
    }
  };

  const refreshScansList = useCallback(async () => {
    try {
      const res = await api.get('/onpage/scans');
      setScans(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  const sortedScans = useMemo(() => {
    const incomplete = (s) => s.status && s.status !== 'complete';
    return [...scans].sort((a, b) => {
      const ai = incomplete(a) ? 1 : 0;
      const bi = incomplete(b) ? 1 : 0;
      if (ai !== bi) return bi - ai;
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [scans]);

  /* ── Actions ──── */
  const startQuickScan = () => {
    if (!userWebsite) { toast.error('Please connect your website first in Integrations'); return; }
    handleRunFullScan({});
  };

  const openScanForm = () => {
    if (!userWebsite) { toast.error('Please connect your website first in Integrations'); return; }
    setScanFormOpen(true);
  };

  const viewScan = async (scan) => {
    setHighlightRecId(null);
    try {
      const res = await api.get(`/onpage/scans/${scan.scan_id}`);
      setCurrentScan(res.data);
      if (res.data.status === 'complete') {
        setActiveScanId(null);
        setScanSteps(INITIAL_STEPS);
      }
    } catch {
      setCurrentScan(scan);
      if (scan.status === 'complete') {
        setActiveScanId(null);
        setScanSteps(INITIAL_STEPS);
      }
    }
  };

  const runStep = async (stepKey, formData = {}) => {
    const url = userWebsite.startsWith('http') ? userWebsite : `https://${userWebsite}`;
    setScanSteps((prev) => ({ ...prev, [stepKey]: { status: 'running', data: null } }));
    onpageScanTask.start();
    const phaseByStep = { crawl: 0, score: 1, recommend: 2 };
    onpageScanTask.set({
      phase: phaseByStep[stepKey],
      ...(stepKey !== 'crawl' && activeScanId ? { scanId: activeScanId } : {}),
    });

    try {
      if (stepKey === 'crawl') {
        const res = await api.post('/onpage/scan/crawl', { url, ...formData, project_id: selectedProjectId || undefined });
        setActiveScanId(res.data.scan_id);
        onpageScanTask.set({ scanId: res.data.scan_id });
        setStepResults((prev) => ({ ...prev, crawl: res.data }));
        setScanSteps((prev) => ({ ...prev, crawl: { status: 'done', data: null } }));
        toast.success('Page crawled successfully');
        await refreshScansList();
      } else if (stepKey === 'score') {
        const scanId = activeScanId;
        const res = await api.post(`/onpage/scan/${scanId}/score`);
        setStepResults((prev) => ({ ...prev, score: res.data }));
        setScanSteps((prev) => ({ ...prev, score: { status: 'done', data: null } }));
        toast.success('SEO scoring complete');
        await refreshScansList();
      } else if (stepKey === 'recommend') {
        const scanId = activeScanId;
        const res = await api.post(`/onpage/scan/${scanId}/recommend`);
        setStepResults((prev) => ({ ...prev, recommend: res.data }));
        setScanSteps((prev) => ({ ...prev, recommend: { status: 'done', data: null } }));
        toast.success('AI recommendations generated');
        await refreshScansList();
      }
      onpageScanTask.complete();
    } catch (err) {
      const detail = detailMessage(err.response?.data?.detail);
      const stepLabels = { crawl: 'Page crawl', score: 'SEO scoring', recommend: 'AI recommendations' };
      const label = stepLabels[stepKey] || stepKey;

      let msg;
      if (!err.response) {
        msg = `${label} couldn't connect to the server. Please check your connection and try again.`;
      } else if (err.response.status >= 500) {
        msg = `${label} ran into a temporary issue on our end. Please try again — or apply this change manually if it keeps failing.`;
      } else {
        msg = detail || `${label} failed. You can retry or apply the change manually from your website's CMS.`;
      }
      toast.error(msg, { duration: 6000 });
      setScanSteps((prev) => ({ ...prev, [stepKey]: { status: 'error', data: null, errorMessage: msg } }));
      onpageScanTask.fail(msg);
    }
  };

  const handleRunFullScan = async (formData = {}) => {
    setScanFormOpen(false);
    setScanFormData(formData);
    setCurrentScan(null);
    /* Set crawl:running in the same state batch so anyRunning is immediately
       true — prevents the empty "Run AI Scan" card from flashing for one frame. */
    setScanSteps({ ...INITIAL_STEPS, crawl: { status: 'running', data: null } });
    setStepResults(EMPTY_RESULTS);
    setIsAutoScan(true);

    const url = userWebsite.startsWith('http') ? userWebsite : `https://${userWebsite}`;
    onpageScanTask.start();

    try {
      const crawlRes = await api.post('/onpage/scan/crawl', { url, ...formData, project_id: selectedProjectId || undefined });
      const scanId = crawlRes.data.scan_id;
      setActiveScanId(scanId);
      onpageScanTask.set({ scanId, phase: 1 });
      setStepResults((prev) => ({ ...prev, crawl: crawlRes.data }));
      setScanSteps((prev) => ({ ...prev, crawl: { status: 'done', data: null } }));
      await refreshScansList();

      setScanSteps((prev) => ({ ...prev, score: { status: 'running', data: null } }));
      const scoreRes = await api.post(`/onpage/scan/${scanId}/score`);
      setStepResults((prev) => ({ ...prev, score: scoreRes.data }));
      setScanSteps((prev) => ({ ...prev, score: { status: 'done', data: null } }));
      await refreshScansList();

      onpageScanTask.set({ phase: 2 });
      setScanSteps((prev) => ({ ...prev, recommend: { status: 'running', data: null } }));
      const recRes = await api.post(`/onpage/scan/${scanId}/recommend`);
      setStepResults((prev) => ({ ...prev, recommend: recRes.data }));
      setScanSteps((prev) => ({ ...prev, recommend: { status: 'done', data: null } }));

      toast.success('Full scan complete!');
      onpageScanTask.complete();
    } catch (err) {
      let msg;
      if (!err.response) {
        msg = "The scan couldn't connect to our servers. Please check your connection and try again.";
      } else if (err.response.status >= 500) {
        msg = "The scan ran into a temporary issue. Please try again in a moment — our team has been notified.";
      } else {
        msg = detailMessage(err.response?.data?.detail) || 'The scan could not be completed. Please try again or run each step individually.';
      }
      toast.error(msg, { duration: 6000 });
      setScanSteps((prev) => {
        const updated = { ...prev };
        for (const k of ['crawl', 'score', 'recommend']) {
          if (updated[k].status === 'running') updated[k] = { status: 'error', data: null, errorMessage: msg };
        }
        return updated;
      });
      setIsAutoScan(false);
      onpageScanTask.fail(msg);
    }
  };

  const handleRunStepByStep = (formData = {}) => {
    setScanFormOpen(false);
    setScanFormData(formData);
    setCurrentScan(null);
    setScanSteps(INITIAL_STEPS);
    setStepResults(EMPTY_RESULTS);
    runStep('crawl', formData);
  };

  const handleDownloadPDF = async () => {
    if (!currentScan?.scan_id) return;
    try {
      toast.info('Generating PDF report…');
      const res = await api.get(`/onpage/scans/${currentScan.scan_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || `seo-audit-${currentScan.scan_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await api.post(`/onpage/recommendations/${id}/approve`);
      const newStatus = res.data?.status || (isClient ? 'client_approved' : 'approved');
      toast.success(
        isClient
          ? 'Approved! Sent to our team for final review.'
          : (res.data?.auto_executed
              ? 'Final approval granted — change applied to your site!'
              : 'Final approval granted.')
      );
      setCurrentScan((prev) => ({
        ...prev,
        recommendations: prev.recommendations.map((r) =>
          r.recommendation_id === id
            ? { ...r, status: newStatus, auto_executed: res.data?.auto_executed || r.auto_executed }
            : r
        ),
      }));
    } catch (err) {
      toast.error(detailMessage(err.response?.data?.detail) || 'Failed to approve recommendation');
    }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/onpage/recommendations/${id}/reject`);
      toast.success('Recommendation rejected');
      setCurrentScan((prev) => ({
        ...prev,
        recommendations: prev.recommendations.map((r) => r.recommendation_id === id ? { ...r, status: 'rejected' } : r),
      }));
    } catch { toast.error('Failed to reject recommendation'); }
  };

  const handleImplement = async (id) => {
    try {
      await api.post(`/onpage/recommendations/${id}/mark-implemented`);
      toast.success('Marked as implemented!');
      setCurrentScan((prev) => ({
        ...prev,
        recommendations: prev.recommendations.map((r) => r.recommendation_id === id ? { ...r, status: 'implemented' } : r),
      }));
    } catch (err) {
      toast.error(detailMessage(err.response?.data?.detail) || 'Failed to mark as implemented', { duration: 6000 });
    }
  };

  const handleExecute = async (id) => {
    try {
      const res = await api.post(`/onpage/recommendations/${id}/execute`);
      toast.success('Change executed and applied to your site!');
      setCurrentScan((prev) => ({
        ...prev,
        recommendations: prev.recommendations.map((r) =>
          r.recommendation_id === id ? { ...r, status: 'implemented', auto_executed: true } : r
        ),
      }));
      return res;
    } catch (err) {
      let msg;
      const detail = detailMessage(err.response?.data?.detail);
      if (!err.response) {
        msg = "Couldn't reach the server. Please check your connection and try again.";
      } else if (err.response.status === 400) {
        msg = detail || "This change requires manual implementation. Use the code snippet in the recommendation.";
      } else if (err.response.status === 422) {
        // Specific reason from the backend — always show it
        msg = detail || "Auto-execution isn't available for this change. Please apply it manually using the code snippet provided.";
      } else if (err.response.status >= 500) {
        msg = detail || "Auto-execution hit a temporary server issue. Please try again, or apply the fix manually using the code snippet in the recommendation.";
      } else {
        msg = detail || "This change couldn't be applied automatically. Use the code snippet in the recommendation to apply it manually.";
      }
      toast.error(msg, { duration: 9000 });
    }
  };

  const handleArchiveScan = async (scanId, e) => {
    e.stopPropagation();
    setArchiveConfirm(scanId);
    setArchiveDialogOpen(true);
  };

  const confirmArchive = async () => {
    const scanId = archiveConfirm;
    setArchiveDialogOpen(false);
    setArchiveConfirm(null);
    try {
      await api.delete(`/onpage/scans/${scanId}`);
      toast.success('Scan archived');
      setScans((prev) => prev.filter((s) => s.scan_id !== scanId));
      if (currentScan?.scan_id === scanId) {
        const remaining = scans.filter((s) => s.scan_id !== scanId);
        setCurrentScan(remaining.length > 0 ? remaining[0] : null);
      }
    } catch { toast.error('Failed to archive scan'); }
  };

  const handleDeleteScan = (scan, e) => {
    e.stopPropagation();
    setDeleteConfirm(scan);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteScan = async () => {
    const scan = deleteConfirm;
    if (!scan?.scan_id) return;

    setDeleteDialogOpen(false);
    setDeleteConfirm(null);
    try {
      await api.delete(`/onpage/scans/${scan.scan_id}/permanent`);
      toast.success('Scan deleted');
      const remainingScans = scans.filter((s) => s.scan_id !== scan.scan_id);
      setScans(remainingScans);
      setArchivedScans((prev) => prev.filter((s) => s.scan_id !== scan.scan_id));
      if (currentScan?.scan_id === scan.scan_id) setCurrentScan(remainingScans[0] || null);
    } catch { toast.error('Failed to delete scan'); }
  };

  const handleRestoreScan = async (scanId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/onpage/scans/${scanId}/restore`);
      toast.success('Scan restored');
      setArchivedScans((prev) => prev.filter((s) => s.scan_id !== scanId));
      const scansRes = await api.get('/onpage/scans');
      setScans(scansRes.data);
      if (!currentScan) setCurrentScan(scansRes.data[0] || null);
    } catch { toast.error('Failed to restore scan'); }
  };

  const toggleArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next) fetchArchivedScans();
  };

  /* ── Derived data ──── */
  const stats = buildStats(currentScan);

  /* ── Loading ──── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  /* ── No website connected ──── */
  /* Only clients need their own connected site to scan. Internal reviewers
     (admin/PM/execution team) review client scans, so let them
     into the dashboard even without a website of their own. */
  if (!userWebsite && isClient) {
    return (
      <div className="space-y-6" data-testid="onpage-seo-dashboard">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">On-Page SEO</h1>
          <p className="text-slate-600 dark:text-slate-400">AI-powered SEO analysis for your website</p>
        </div>
        <Card className="gds-card">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Connect Your Website First</h3>
            <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
              To run AI-powered SEO analysis, please connect your website in Integrations.
            </p>
            <Button className="gds-btn-primary" onClick={() => navigate('/dashboard/integrations')}>
              <Settings className="w-4 h-4 mr-2" />Go to Integrations
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── Main render ──── */
  return (
    <div className="space-y-6" data-testid="onpage-seo-dashboard">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">On-Page SEO</h1>
          <p className="text-slate-600 dark:text-slate-400">AI-powered recommendations for your website</p>
        </div>
        <div className="flex items-center gap-3">
          {currentScan && !anyRunning && (
            <>
              <Button variant="outline" onClick={handleDownloadPDF}>
                <Download className="w-5 h-5 mr-2" />Download PDF
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard/reports', { state: { tab: 'seo-audit' } })}>
                <FileText className="w-5 h-5 mr-2" />View Full Report
              </Button>
            </>
          )}
          <Button className="gds-btn-primary" onClick={startQuickScan} disabled={anyRunning}>
            {anyRunning
              ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Scanning…</>
              : <><Sparkles className="w-5 h-5 mr-2" />Run AI Scan</>}
          </Button>
          {!anyRunning && (
            <Button variant="outline" onClick={openScanForm} title="Configure keywords & page type before scanning">
              <Settings className="w-5 h-5 mr-2" />Configure
            </Button>
          )}
        </div>
      </div>

      {/* ── Connected Website Banner ─────────────────────────────────────── */}
      {userWebsite && (
        <Card className="gds-card border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium">Connected Website</p>
                  <a href={userWebsite.startsWith('http') ? userWebsite : `https://${userWebsite}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                    {userWebsite}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="w-3 h-3 mr-1" />Connected
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Main Layout: Sidebar + Content ───────────────────────────────── */}
      <div className="grid lg:grid-cols-4 gap-6">

        {/* Scan History Sidebar */}
        <Card className="gds-card lg:col-span-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading">Scan History</CardTitle>
              <button
                onClick={toggleArchived}
                className={`p-1.5 rounded-lg transition-colors ${showArchived ? 'bg-amber-100 text-amber-700' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                title={showArchived ? 'Show active scans' : 'Show archived scans'}
              >
                <Archive className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            {showArchived ? (
              archivedScans.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Archive className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No archived scans</p>
                </div>
              ) : archivedScans.map((scan) => (
                <ScanHistoryItem
                  key={scan.scan_id}
                  scan={scan}
                  archived
                  active={false}
                  onSelect={() => {}}
                  onArchive={handleArchiveScan}
                  onRestore={handleRestoreScan}
                  onDelete={handleDeleteScan}
                />
              ))
            ) : scans.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No scans yet</p>
                <p className="text-xs mt-1">Run your first AI scan</p>
              </div>
            ) : sortedScans.map((scan) => (
              <ScanHistoryItem
                key={scan.scan_id}
                scan={scan}
                active={currentScan?.scan_id === scan.scan_id || activeScanId === scan.scan_id}
                onSelect={viewScan}
                onArchive={handleArchiveScan}
                onRestore={handleRestoreScan}
                onDelete={handleDeleteScan}
              />
            ))}
          </CardContent>
        </Card>

        {/* ── Results Column ──────────────────────────────────────────── */}
        <div className="lg:col-span-3 space-y-6">
          {(anyRunning || (activeScanId && !allIdle)) ? (
            <ScanStepsAccordion
              steps={scanSteps}
              stepResults={stepResults}
              onRunStep={(key) => runStep(key, scanFormData)}
              anyRunning={anyRunning}
              isAutoScan={isAutoScan}
            />
          ) : currentScan ? (
            <>
              {/* ── Stat Cards ─── */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard value={stats.total} label="Total Issues" delay={0} />
                {currentScan.audit_scores && (
                  <StatCard
                    value={currentScan.audit_scores.overall_score}
                    label="Overall Score"
                    colorClass={GRADE_COLORS[currentScan.audit_scores.grade] || 'text-slate-600'}
                    delay={0.05}
                  />
                )}
                <StatCard value={stats.pending} label="Pending" colorClass="text-amber-600" delay={0.1} />
                <StatCard value={stats.approved} label="Approved" colorClass="text-emerald-600" delay={0.15} />
                <StatCard value={stats.implemented} label="Implemented" colorClass="text-indigo-600" delay={0.2} />
              </div>

              {/* ── Hero Score Card (gauge + radar + timeline) ─── */}
              {currentScan.audit_scores && (
                <motion.div {...fadeUp(0.1)}>
                  <AuditScoreCard auditScores={currentScan.audit_scores} scans={scans} />
                </motion.div>
              )}

              {/* ── Page Overview ─── */}
              {currentScan.page_data && (
                <motion.div {...fadeUp(0.15)}>
                  <PageDataPanel pageData={currentScan.page_data} auditScores={currentScan.audit_scores} />
                </motion.div>
              )}

              {/* ── Section Breakdown ─── */}
              {currentScan.audit_scores?.sections && (
                <motion.div {...fadeUp(0.2)}>
                  <SectionScoresCard sections={currentScan.audit_scores.sections} />
                </motion.div>
              )}

              {/* ── Core Web Vitals ─── */}
              {currentScan.page_data?.core_web_vitals?.available && (
                <motion.div {...fadeUp(0.22)}>
                  <CoreWebVitalsPanel cwv={currentScan.page_data.core_web_vitals} />
                </motion.div>
              )}
              {currentScan.page_data?.core_web_vitals?.pending && (
                <motion.div {...fadeUp(0.22)}>
                  <Card>
                    <CardContent className="flex items-center gap-3 py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-400" />
                      <span className="text-sm text-gray-400">Fetching Core Web Vitals from PageSpeed Insights…</span>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* ── Usability ─── */}
              {currentScan.page_data?.usability && (
                <motion.div {...fadeUp(0.24)}>
                  <UsabilityPanel usability={currentScan.page_data.usability} />
                </motion.div>
              )}

              {/* ── Infrastructure Row: Security + Robots/Sitemap ─── */}
              {(currentScan.page_data?.security_headers || currentScan.page_data?.robots_txt) && (
                <motion.div {...fadeUp(0.24)} className="grid md:grid-cols-2 gap-6">
                  <SecurityHeadersPanel securityHeaders={currentScan.page_data.security_headers} />
                  <RobotsSitemapPanel robotsTxt={currentScan.page_data.robots_txt} sitemap={currentScan.page_data.sitemap} />
                </motion.div>
              )}

              {/* ── Content Row: Readability + AI Readiness ─── */}
              {(currentScan.page_data?.readability || currentScan.page_data?.ai_readiness) && (
                <motion.div {...fadeUp(0.26)} className="grid md:grid-cols-2 gap-6">
                  <ReadabilityPanel readability={currentScan.page_data.readability} />
                  <AIReadinessPanel aiReadiness={currentScan.page_data.ai_readiness} />
                </motion.div>
              )}

              {/* ── Performance & Resources ─── */}
              {currentScan.page_data && (
                <motion.div {...fadeUp(0.28)}>
                  <PerformanceResourcePanel pageData={currentScan.page_data} />
                </motion.div>
              )}

              {/* ── Redirects & Mixed Content ─── */}
              {(currentScan.page_data?.redirects?.length > 0 || currentScan.page_data?.mixed_content?.length > 0) && (
                <motion.div {...fadeUp(0.29)}>
                  <RedirectsMixedContentPanel
                    redirects={currentScan.page_data.redirects}
                    mixedContent={currentScan.page_data.mixed_content}
                  />
                </motion.div>
              )}

              {/* ── Tech + Social + Hreflang + Code Quality Row ─── */}
              {(currentScan.page_data?.technologies?.length > 0 || currentScan.page_data?.social_links?.length > 0) && (
                <motion.div {...fadeUp(0.30)} className="grid md:grid-cols-2 gap-6">
                  <TechnologyPanel technologies={currentScan.page_data.technologies} />
                  <SocialLinksPanel socialLinks={currentScan.page_data.social_links} />
                </motion.div>
              )}

              {/* ── Hreflang + Code Quality Row ─── */}
              {(currentScan.page_data?.hreflang_tags?.length > 0 || currentScan.page_data?.deprecated_tags?.length > 0 || currentScan.page_data?.plaintext_emails?.length > 0) && (
                <motion.div {...fadeUp(0.31)} className="grid md:grid-cols-2 gap-6">
                  <HreflangPanel hreflangTags={currentScan.page_data.hreflang_tags} />
                  <CodeQualityPanel
                    deprecatedTags={currentScan.page_data.deprecated_tags}
                    plaintextEmails={currentScan.page_data.plaintext_emails}
                  />
                </motion.div>
              )}

              {/* ── Render-Blocking + Caching/Minification ─── */}
              {(currentScan.page_data?.render_blocking?.length > 0 || currentScan.page_data?.caching || currentScan.page_data?.minification) && (
                <motion.div {...fadeUp(0.32)} className="grid md:grid-cols-2 gap-6">
                  <RenderBlockingPanel renderBlocking={currentScan.page_data.render_blocking} />
                  <CachingMinificationPanel
                    caching={currentScan.page_data.caching}
                    minification={currentScan.page_data.minification}
                  />
                </motion.div>
              )}

              {/* ── Broken Links ─── */}
              {currentScan.page_data?.broken_links?.length > 0 && (
                <motion.div {...fadeUp(0.33)}>
                  <BrokenLinksPanel brokenLinks={currentScan.page_data.broken_links} />
                </motion.div>
              )}

              {/* ── SSL + Analytics/CDN Row ─── */}
              {(currentScan.page_data?.ssl_info || currentScan.page_data?.analytics_tools?.length > 0 || currentScan.page_data?.cdn) && (
                <motion.div {...fadeUp(0.34)} className="grid md:grid-cols-2 gap-6">
                  <SSLCertPanel sslInfo={currentScan.page_data.ssl_info} />
                  <AnalyticsCDNPanel
                    analyticsTools={currentScan.page_data.analytics_tools}
                    cdn={currentScan.page_data.cdn}
                    httpVersion={currentScan.page_data.http_version}
                  />
                </motion.div>
              )}

              {/* ── Font Optimization + Iframes Row ─── */}
              {(currentScan.page_data?.font_info?.total_fonts > 0 || currentScan.page_data?.iframes?.length > 0) && (
                <motion.div {...fadeUp(0.35)} className="grid md:grid-cols-2 gap-6">
                  <FontOptimizationPanel fontInfo={currentScan.page_data.font_info} />
                  <IframesPanel iframes={currentScan.page_data.iframes} />
                </motion.div>
              )}

              {/* ── Category Donut + Impact/Effort Matrix ─── */}
              {currentScan.recommendations?.length > 0 && (
                <motion.div {...fadeUp(0.25)} className="grid md:grid-cols-2 gap-6">
                  <CategoryDonut recommendations={currentScan.recommendations} />
                  <ImpactEffortMatrix recommendations={currentScan.recommendations} />
                </motion.div>
              )}

              {/* ── AI Recommendations ─── */}
              <motion.div {...fadeUp(0.3)} ref={recommendationsRef} id="ai-recommendations" className="scroll-mt-24">
                <RecommendationsPanel
                  recommendations={currentScan.recommendations || []}
                  scanUrl={currentScan.url}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onImplement={handleImplement}
                  onExecute={handleExecute}
                  isClient={isClient}
                  canFinalApprove={canFinalApprove}
                  canImplement={canImplement}
                  stats={stats}
                  highlightRecId={highlightRecId}
                />
              </motion.div>
            </>
          ) : (
            /* ── Empty state ─── */
            <Card className="gds-card">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">Ready to Analyse</h3>
                <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
                  Click <strong>Run AI Scan</strong> to analyse your website. Our AI will crawl your pages and generate specific SEO recommendations with implementation code.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Button className="gds-btn-primary" onClick={startQuickScan} disabled={anyRunning}>
                    {anyRunning
                      ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Scanning…</>
                      : <><Sparkles className="w-5 h-5 mr-2" />Run AI Scan</>}
                  </Button>
                  <Button variant="outline" onClick={openScanForm} disabled={anyRunning}>
                    <Settings className="w-4 h-4 mr-2" />Configure
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <ScanFormDialog
        open={scanFormOpen}
        onClose={() => setScanFormOpen(false)}
        onSubmit={handleRunFullScan}
        onSubmitStepByStep={handleRunStepByStep}
        scanning={anyRunning}
        websiteUrl={userWebsite}
      />

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this scan?</AlertDialogTitle>
            <AlertDialogDescription>
              This scan will be moved to your archive. You can restore it anytime from the archived scans view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmArchive} className="bg-amber-600 hover:bg-amber-700">
              <Archive className="w-4 h-4 mr-2" />Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this scan?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the scan for {deleteConfirm?.url || 'this page'}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteScan} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-2" />Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
